import { useMemo } from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartEmptyState,
  ChartErrorState,
  ChartLoadingState,
} from "../ChartStates";
import { ChartBreakdownLegend, breakdownShares } from "../ChartBreakdownLegend";
import { cn } from "../../ui/utils";
import {
  AXIS_TICK_FONT_SIZE,
  AXIS_TICK_FONT_WEIGHT,
  BAR_FILL,
  CHART_PALETTE_LIGHT,
  MARK_CORNER_RADIUS,
  MARK_STROKE_WIDTH,
  formatCompact,
  formatNumber,
  markGlow,
} from "../chartUtils";

/**
 * Horizontal bar chart for a single measure ranked highest to lowest, where
 * each bar carries its own accent.
 *
 * The sibling {@link BarChart} colours by *series* — correct when bars are
 * grouped or stacked, since colour then means "which measure". Here there is
 * only one measure and colour means "which rank", so it has to vary per bar.
 * That is the whole reason this is a separate chart rather than a flag.
 */
/**
 * Space between a bar's cap and its count. Wide enough to clear the cap's
 * corner radius AND the glow bleeding past it, so the numeral sits on clean
 * background rather than in the halo.
 */
const LABEL_GAP = 10;

export interface RankedBarDatum {
  name: string;
  value: number;
  /** Per-bar accent. Falls back to the palette, cycled by rank. */
  color?: string;
}

export interface RankedBarChartProps {
  data: RankedBarDatum[];
  height?: number;
  palette?: readonly string[];
  loading?: boolean;
  error?: string | Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  /** Width reserved for the category axis labels. */
  categoryWidth?: number;
  barSize?: number;
  /** Axis + on-bar value text. Defaults to compact notation (1.2K). */
  valueFormatter?: (value: number) => string;
  /** Tooltip value text. Defaults to the full, grouped number. */
  tooltipValueFormatter?: (value: number) => string;
  /** Noun for the tooltip's value row, e.g. "Findings". */
  valueLabel?: string;
  /**
   * Show the colour-coded breakdown legend beside the plot: one row per bar
   * carrying its value and share of the total. Off by default, because the
   * bars already carry their category labels on the axis -- turn it on when
   * the shares are part of the reading, not just the ranking.
   */
  showBreakdownLegend?: boolean;
  /**
   * Denominator for each row's share. Omit to take shares over the shown bars.
   * Pass a grand total when these are a Top-N slice of something larger, so
   * the shares correctly do NOT sum to 100.
   */
  breakdownTotal?: number;
  className?: string;
  ariaLabel?: string;
}

export function RankedBarChart({
  data,
  height = 270,
  palette = CHART_PALETTE_LIGHT,
  loading,
  error,
  onRetry,
  emptyMessage = "No data available",
  categoryWidth = 110,
  barSize = 18,
  valueFormatter = formatCompact,
  tooltipValueFormatter = formatNumber,
  valueLabel = "Value",
  showBreakdownLegend = false,
  breakdownTotal,
  className,
  ariaLabel,
}: RankedBarChartProps) {
  // Rank highest → lowest so the accent follows magnitude. Recharts draws the
  // first datum of a horizontal chart at the TOP, so no reversal is needed.
  const ranked = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    return [...rows]
      .sort((a, b) => b.value - a.value)
      .map((d, i) => ({
        name: d.name,
        value: d.value,
        color: d.color ?? palette[i % palette.length],
      }));
  }, [data, palette]);

  const errorMessage = error instanceof Error ? error.message : error;
  if (errorMessage) {
    return <ChartErrorState height={height} message={errorMessage} onRetry={onRetry} />;
  }
  if (loading) return <ChartLoadingState height={height} shape="cartesian" />;
  if (!ranked.length || ranked.every((d) => d.value <= 0)) {
    return <ChartEmptyState height={height} message={emptyMessage} shape="cartesian" />;
  }

  // Shares over the grand total when these bars are a Top-N slice, otherwise
  // over the bars themselves (largest-remainder, so they sum to exactly 100).
  const sliceOfLargerSet = Boolean(breakdownTotal && breakdownTotal > 0);
  const shares = sliceOfLargerSet
    ? ranked.map((d) => Math.round((d.value / (breakdownTotal as number)) * 100))
    : breakdownShares(ranked.map((d) => d.value));

  return (
    <div
      className={cn("flex min-w-0 flex-col gap-2", className)}
      style={{ height }}
      aria-label={ariaLabel}
      role="img"
    >
      {showBreakdownLegend && (
        <ChartBreakdownLegend
          items={ranked.map((d, i) => ({
            name: d.name,
            color: d.color,
            value: d.value,
            share: shares[i] ?? 0,
          }))}
          valueFormatter={valueFormatter}
        />
      )}

      <div className="min-w-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={ranked}
          layout="vertical"
          margin={{ top: 8, right: 36, bottom: 8, left: 8 }}
        >
          <XAxis
            type="number"
            allowDecimals={false}
            tick={{
              fontSize: AXIS_TICK_FONT_SIZE,
              fontWeight: AXIS_TICK_FONT_WEIGHT,
              fill: "var(--muted-foreground)",
            }}
            // Wrapped, NOT passed by reference: recharts calls a tickFormatter
            // as (value, index), and `formatCompact`'s second parameter is a
            // unit suffix -- handing it the index rendered tick 5 as "51".
            tickFormatter={(value: number) => valueFormatter(value)}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={categoryWidth}
            tick={{
              fontSize: AXIS_TICK_FONT_SIZE,
              fontWeight: AXIS_TICK_FONT_WEIGHT,
              fill: "var(--muted-foreground)",
            }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "var(--muted)", opacity: 0.4 }}
            formatter={(value: number) => [tooltipValueFormatter(value), valueLabel]}
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              color: "var(--popover-foreground)",
              fontSize: 12,
            }}
            // Recharts inks a tooltip row in the mark's own fill, and these
            // bars are outline-only -- a transparent fill made the row text
            // invisible. Both lines are pinned to the popover ink instead.
            labelStyle={{ color: "var(--popover-foreground)", fontWeight: 600 }}
            itemStyle={{ color: "var(--popover-foreground)" }}
          />
          <Bar
            dataKey="value"
            barSize={barSize}
            // Rounded at the data end, square at the axis it grows from.
            radius={[0, MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0]}
            isAnimationActive={false}
          >
            {/* Outline-only: no fill, just the stroke in the rank's accent
                plus a soft glow of the same colour, so the shortest bar in the
                ranking still has a definite edge. */}
            {ranked.map((d) => (
              <Cell
                key={d.name}
                fill={BAR_FILL}
                stroke={d.color}
                strokeWidth={MARK_STROKE_WIDTH}
                style={{ filter: markGlow(d.color) }}
              />
            ))}

            {/* The count is drawn by hand rather than left to recharts' label
                props. Both `<Bar label>` and a plain `<LabelList fill>` render
                the numeral inside the bar's own group, where the Cell's SVG
                filter applies to it as well -- an SVG filter covers the element
                AND its descendants, which is why setting `fill` alone never
                took: the glow was still smearing a bar-coloured blob over the
                cap. Owning the <text> lets it opt out with `filter: none`. */}
            <LabelList
              dataKey="value"
              content={(props: unknown) => {
                const { x, y, width, height, value } = (props ?? {}) as Record<
                  string,
                  number | undefined
                >;
                if (
                  typeof x !== "number" ||
                  typeof y !== "number" ||
                  typeof width !== "number" ||
                  typeof height !== "number" ||
                  typeof value !== "number"
                ) {
                  return null;
                }
                return (
                  <text
                    x={x + width + LABEL_GAP}
                    y={y + height / 2}
                    textAnchor="start"
                    dominantBaseline="middle"
                    fill="var(--foreground)"
                    fontSize={11}
                    fontWeight={700}
                    style={{ filter: "none" }}
                  >
                    {valueFormatter(value)}
                  </text>
                );
              }}
            />
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}
